import { Component, EventEmitter, Output, ViewChild, ElementRef, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SocialService } from '../../../../../../../../services/social.service';
import { ToastService } from '../../../../../../../../services/toast.service';
import { BackButtonComponent } from '../../../../../../../../shared/components/buttons/back-button/back-button.component';
import { ConfirmButtonComponent } from '../../../../../../../../shared/components/buttons/confirm-button/confirm-button.component';
import { CameraIconButtonComponent } from '../../../../../../../../shared/components/buttons/camera-button/camera-button.component';
import { RestrictiveAcknowledgementDialogComponent } from '../../../../../../../../shared/components/restrictive-acknowledgement-dialog/restrictive-acknowledgement-dialog.component';


@Component({
  selector: 'app-create-fleet',
  standalone: true,
  imports: [CommonModule, FormsModule, BackButtonComponent, ConfirmButtonComponent, CameraIconButtonComponent, RestrictiveAcknowledgementDialogComponent],
  templateUrl: './create-fleet.component.html',
  styleUrl: './create-fleet.component.scss'
})
export class CreateFleetComponent implements OnInit {
  @Input() editFleet: any;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<any>();

  @ViewChild('picInput') picInput!: ElementRef;
  @ViewChild('coverInput') coverInput!: ElementRef;

  fleetData = {
    name: '',
    category: 'Fishing',
    home_port: '',
    description: '',
    require_approval: true,
    isPrivate: true,
    isRestrictive: false,
    message: ''
  };

  homePortQuery = '';
  homePortResults: Array<{ displayName: string }> = [];
  homePortLoading = false;
  showHomePortDropdown = false;
  private homePortSearchTimer: any = null;

  showRestrictiveDialog = false;
  restrictiveDialogMode: 'agreement' | 'rules' = 'agreement';

  fleetPicFile: File | null = null;
  coverPhotoFile: File | null = null;
  fleetPicPreview: string | null = null;
  coverPhotoPreview: string | null = null;

  categories = ['Fishing', 'Sailing', 'Racing', 'Cruising', 'Commercial', 'Research'];

  public categoryIcons: Record<string, string> = {
    fishing: 'assets/common_icons/fish-food.svg',
    sailing: 'assets/common_icons/sailboat-offshore.svg',
    racing: 'assets/common_icons/ferry-boat.svg',
    cruising: 'assets/common_icons/boat.svg',
    commercial: 'assets/common_icons/store-02.svg',
    research: 'assets/common_icons/search-list-01.svg'
  };

  // Loading flag for the Save/Create confirm button.
  isSaving = false;

  constructor(
    private socialService: SocialService,
    public toast: ToastService,
  ) { }

  ngOnInit() {
    if (this.editFleet) {
      this.fleetData = {
        name: this.editFleet.name || '',
        category: this.editFleet.category || 'Fishing',
        home_port: this.editFleet.home_port || '',
        description: this.editFleet.description || '',
        require_approval: true,
        isPrivate: this.editFleet.privacy === 'private',
        isRestrictive: this.editFleet.is_restrictive ?? false,
        message: ''
      };
      this.homePortQuery = this.fleetData.home_port;
      this.fleetPicPreview = this.editFleet.image_url;
      this.coverPhotoPreview = this.editFleet.cover_photo_url;
    }
  }

  onHomePortInput(query: string) {
    this.fleetData.home_port = query;
    clearTimeout(this.homePortSearchTimer);
    if (!query.trim()) {
      this.homePortResults = [];
      this.showHomePortDropdown = false;
      return;
    }
    this.homePortLoading = true;
    this.homePortSearchTimer = setTimeout(() => this.doHomePortSearch(query), 400);
  }

  private async doHomePortSearch(query: string) {
    try {
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=8&lang=en`;
      const res = await fetch(url);
      if (!res.ok) { this.homePortLoading = false; return; }
      const data = await res.json();
      const features: any[] = data.features || [];
      const seen = new Set<string>();
      this.homePortResults = [];
      for (const f of features) {
        const p = f.properties || {};
        const parts: string[] = [];
        if (p.name) parts.push(p.name);
        if (p.city && p.city !== p.name) parts.push(p.city);
        const stateAbbr = this.getStateAbbr(p.state, p.countrycode);
        if (stateAbbr) parts.push(stateAbbr);
        if (p.countrycode && p.countrycode !== 'US' && p.countrycode !== 'CA') parts.push(p.countrycode);
        const displayName = parts.join(', ');
        if (displayName && !seen.has(displayName)) {
          seen.add(displayName);
          this.homePortResults.push({ displayName });
        }
      }
      this.showHomePortDropdown = this.homePortResults.length > 0;
    } catch {
      this.homePortResults = [];
    } finally {
      this.homePortLoading = false;
    }
  }

  selectHomePort(result: { displayName: string }) {
    this.fleetData.home_port = result.displayName;
    this.homePortQuery = result.displayName;
    this.homePortResults = [];
    this.showHomePortDropdown = false;
  }

  hideHomePortDropdown() {
    setTimeout(() => { this.showHomePortDropdown = false; }, 200);
  }

  private getStateAbbr(stateName: string, countrycode: string): string {
    if (!stateName) return '';
    if (countrycode !== 'US' && countrycode !== 'CA') return stateName;
    const map: Record<string, string> = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
      'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
      'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
      'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
      'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
      'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
      'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
      'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
      'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
      'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
      'Wisconsin': 'WI', 'Wyoming': 'WY',
      'Alberta': 'AB', 'British Columbia': 'BC', 'Manitoba': 'MB', 'New Brunswick': 'NB',
      'Newfoundland and Labrador': 'NL', 'Nova Scotia': 'NS', 'Ontario': 'ON',
      'Prince Edward Island': 'PE', 'Quebec': 'QC', 'Saskatchewan': 'SK',
    };
    return map[stateName] || stateName;
  }

  closePopup() {
    this.close.emit();
  }

  onRestrictiveClick(event: MouseEvent) {
    event.preventDefault();
    if (!this.fleetData.isRestrictive) {
      this.restrictiveDialogMode = 'agreement';
      this.showRestrictiveDialog = true;
    } else {
      this.fleetData.isRestrictive = false;
    }
  }

  onRestrictiveAgreed() {
    this.showRestrictiveDialog = false;
    this.fleetData.isRestrictive = true;
  }

  onRestrictiveDisagreed() {
    this.showRestrictiveDialog = false;
    this.fleetData.isRestrictive = false;
  }

  onRestrictiveClosed() {
    this.showRestrictiveDialog = false;
  }

  showRestrictiveRules() {
    this.restrictiveDialogMode = 'rules';
    this.showRestrictiveDialog = true;
  }

  setCategory(cat: string) {
    this.fleetData.category = cat;
  }

  triggerUpload(type: 'pic' | 'cover') {
    if (type === 'pic') this.picInput.nativeElement.click();
    else this.coverInput.nativeElement.click();
  }

  onFileSelected(event: any, type: 'pic' | 'cover') {
    const file = event.target.files[0];
    if (file) {
      if (type === 'pic') {
        this.fleetPicFile = file;
        const reader = new FileReader();
        reader.onload = (e: any) => this.fleetPicPreview = e.target.result;
        reader.readAsDataURL(file);
      } else {
        this.coverPhotoFile = file;
        const reader = new FileReader();
        reader.onload = (e: any) => this.coverPhotoPreview = e.target.result;
        reader.readAsDataURL(file);
      }
    }
  }

  saveFleet() {
    if (this.isSaving) return;
    if (!this.fleetData.name?.trim()) {
      this.showError('Fleet Name is required');
      return;
    }
    if (!this.fleetData.home_port?.trim()) {
      this.showError('Home Port is required');
      return;
    }
    this.isSaving = true;
    const formData = new FormData();
    formData.append('name', this.fleetData.name);
    formData.append('description', this.fleetData.description?.trim() || 'A new fleet');
    formData.append('community_type', 'fleet');
    formData.append('privacy', this.fleetData.isPrivate ? 'private' : 'public');
    formData.append('home_port', this.fleetData.home_port);
    formData.append('category', this.fleetData.category.toLowerCase());
    formData.append('is_restrictive', this.fleetData.isRestrictive ? 'true' : 'false');

    if (this.coverPhotoFile) {
      formData.append('community_cover', this.coverPhotoFile);
    }

    if (this.fleetPicFile) {
      formData.append('community_image', this.fleetPicFile);
    }

    const request = this.editFleet
      ? this.socialService.updateFleet(this.editFleet._id, formData)
      : this.socialService.createFleet(formData);

    request.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        // Close the sheet right away — the toast lives at the app root, so it stays
        // visible after the modal dismisses (no awkward multi-second delay).
        this.saved.emit(res);
        this.closePopup();
        this.showSuccess(`Fleet ${this.editFleet ? 'updated' : 'created'} successfully`);
      },
      error: (err: any) => {
        this.isSaving = false;
        console.error(`Error ${this.editFleet ? 'updating' : 'creating'} fleet:`, err);
        this.showError(`Error ${this.editFleet ? 'updating' : 'creating'} fleet`);
      }
    });
  }

  // ---------------- SNACKBAR HELPERS ----------------
  private showError(message: string) {
    this.toast.error(message);
  }

  private showSuccess(message: string) {
    this.toast.success(message);
  }

  getCategoryIcon(category: string): string {
    if (!category) return '';
    return this.categoryIcons[category.toLowerCase()] || '';
  }
}
